/**
 * Modus C — de melding over het volledige scherm.
 *
 * Spec: specs/20260831-domotica-meldingen-bewonerstablet/spec.md FR-15 t/m FR-19
 * Plan: §6.3, §6.4, §6.5, §7.4
 * Taken: T-3.01, T-3.02, T-3.03, T-3.04, T-3.05, T-3.09
 *
 * ── HET IS EEN OVERLAY, GEEN ROUTE ──────────────────────────────────────────
 *
 * ⚠ Dit is de belangrijkste bouwbeslissing van fase 3, en hij is bewust genomen. Een route
 * (`/notification/:id`) zou de navigatiestack vervuilen: na het sluiten komt de bewoner
 * ergens uit waar hij niet vandaan kwam, de terugknop van de WebView krijgt een extra stap,
 * en — het zwaarste argument — bij een crash of een WebView-herstart hérstelt de browser de
 * laatste URL en staat de tablet op een meldingspagina van een melding die allang weg is.
 * Op een wandtablet zonder toetsenbord is dat een toestel dat een monteur nodig heeft.
 *
 * Een overlay heeft dat probleem niet: hij bestaat zolang de store hem claimt en verdwijnt
 * daarna. De pagina eronder is nooit verlaten.
 *
 * ── ÉÉN TEGELIJK ────────────────────────────────────────────────────────────
 *
 * (T-3.05 / OQ-11.) De selectie staat in `NotificationStore.reconsiderClaim()`, niet hier:
 * hij moet ook kloppen wanneer er geen component gemonteerd is. De hoogste prioriteit wint;
 * de rest blijft in de lijst staan en telt mee in de badge. Een WACHTRIJ van volledige
 * schermen zou de tablet onbruikbaar maken tot alles is weggeklikt.
 *
 * ── PAUZEREN BIJ EEN OPROEP, NIET SLUITEN ───────────────────────────────────
 *
 * (T-3.04.) Een oproep ligt op 9990 en deze laag op 9960, dus het gespreksscherm valt er
 * hoe dan ook overheen. Wat hier gebeurt is dat de AFTELLING stopt en de TTS zwijgt. Zonder
 * dat zou een melding van dertig seconden tijdens een gesprek van twee minuten ongezien
 * aflopen — en dan is "displayed" waar terwijl niemand hem gelezen heeft. Na het gesprek
 * loopt hij verder, tenzij `expiresAt` intussen gepasseerd is; dan is hij niet meer
 * relevant en verdwijnt hij zonder bevestiging.
 *
 * ── DE TEKST STAAT NOOIT OP HET BEELD ───────────────────────────────────────
 *
 * (T-3.02.) Op willekeurig beeldmateriaal is geen enkele tekstkleur voorspelbaar leesbaar:
 * een lichte foto maakt witte tekst onzichtbaar, een donkere zwarte. Daarom staat de tekst
 * op een OPAAK paneel in `--caro-card-bg` met `--caro-text` erop. Dat is het kleurenpaar
 * dat het thema zelf garandeert, in licht, donker én hoog-contrast — een halfdoorzichtig
 * paneel zou het contrast laten afhangen van wat er toevallig achter staat, en dat is
 * precies wat 4.5:1 onmogelijk maakt om te garanderen.
 *
 * ── EEN FALENDE ACHTERGROND LAAT DE MELDING STAAN ───────────────────────────
 *
 * (T-3.09.) De tekst IS de melding. Faalt een afbeelding, dan wordt dat via de ack naar
 * `deliveries.last_error` geschreven — meetbaar in plaats van bespeculeerd — en verandert
 * er verder niets aan wat de bewoner ziet.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Z_INDEX } from '../constants/zIndex';
import * as NotificationStore from '../services/NotificationStore';
import { useClaimedNotification } from '../hooks/useResidentNotifications';
import { useCurrentCall } from '../hooks/useSip';
import { useDoorbellCameras } from '../hooks/useDoorbellCameras';
import { NotificationBackgroundCarousel } from './NotificationBackgroundCarousel';
import { NotificationActions } from './NotificationActions';
import { useLocalizedNotification } from '../hooks/useLocalizedNotification';
import { useTranslation } from '../hooks/useTranslation';
import { tf, type Translations } from '../i18n';
import type {
  ResidentNotificationPayload,
  ResidentNotificationPriority,
} from '@caro/shared/types';

/** Prioriteit → thematoken. Nooit een hardgecodeerde hex — zie `NotificationDialog`. */
const PRIORITY_ACCENT: Record<ResidentNotificationPriority, string> = {
  urgent: 'var(--caro-error)',
  high: 'var(--caro-warning)',
  normal: 'var(--caro-primary)',
  low: 'var(--caro-success)',
};

/** Kleur is nooit de enige drager: er hoort een icoon én een woord bij. */
const PRIORITY_ICON: Record<ResidentNotificationPriority, string> = {
  urgent: '⚠',
  high: '❗',
  normal: 'ℹ',
  low: '·',
};

/** Het woord bij de prioriteit, in de taal van de bewoner (T011). */
function priorityWord(t: Translations, p: ResidentNotificationPriority): string {
  const n = t.notifications;
  return p === 'urgent' ? n.priorityUrgent
    : p === 'high' ? n.priorityHigh
      : p === 'low' ? n.priorityLow
        : n.priorityNormal;
}

/** Eén tik per 200ms: zichtbaar vloeiend, rustig genoeg voor een trage WebView. */
const TICK_MS = 200;

export function NotificationFullPage() {
  const claimed = useClaimedNotification();
  const payload = claimed ? claimed.notification : null;

  // Alleen de volledig-schermmodus. `dialog` heeft zijn eigen component; hem hier
  // stilzwijgend paginabreed tonen zou betekenen dat een automation-auteur iets anders
  // krijgt dan hij configureerde — en een volledig scherm is wezenlijk opdringeriger dan
  // een dialoog.
  if (!payload || payload.presentation !== 'fullpage') return null;

  return <FullPageCard key={payload.notificationId} payload={payload} />;
}

function FullPageCard({ payload }: { payload: ResidentNotificationPayload }) {
  const { t } = useTranslation();
  // De tekst in de taal van de bewoner (T033); wisselt mee bij een taalwissel.
  const localized = useLocalizedNotification(payload) || { title: payload.title, body: payload.body, actionLabels: {} };
  const notificationId = payload.notificationId;
  const accent = PRIORITY_ACCENT[payload.priority] || PRIORITY_ACCENT.normal;

  // ── Pauzeren bij een oproep (T-3.04) ─────────────────────────────────────
  //
  // `useCurrentCall()` is een LEES-abonnement op dezelfde modulestatus als `useSip()`,
  // zonder de effecten die daar SIP verbinden en bij uitloggen weer afbreken. Een tweede
  // `useSip()` monteren zou die effecten verdubbelen op een component dat niets met
  // telefonie te maken heeft.
  const call = useCurrentCall();
  const paused = call !== null;

  const totalMs =
    payload.requiresAck || payload.displaySeconds == null
      ? null
      : Math.max(1, payload.displaySeconds) * 1000;

  /** Hoeveel schermtijd er nog over is. Loopt NIET door tijdens een gesprek. */
  const [remainingMs, setRemainingMs] = useState<number | null>(totalMs);
  const lastTickAt = useRef<number>(Date.now());

  const close = useCallback(
    (acknowledge: boolean) => NotificationStore.dismissClaimed(notificationId, acknowledge),
    [notificationId],
  );

  // ── De ack komt NA het schilderen, het geluid daarna ─────────────────────
  //
  // Twee geneste frames, exact zoals in `NotificationDialog`: de eerste wordt tijdens de
  // commit aangevraagd, de tweede loopt pas nadat de browser echt getekend heeft. Eén frame
  // is op een trage WebView niet altijd genoeg, en dan zou `displayed` weer een belofte zijn
  // in plaats van een waarneming.
  useEffect(() => {
    let cancelled = false;
    const outer = requestAnimationFrame(() => {
      const inner = requestAnimationFrame(() => {
        if (cancelled) return;
        NotificationStore.presentAndSound(payload);
      });
      void inner;
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(outer);
    };
    // Op het ID, niet op het object: een `replace` van dezelfde melding mag geen tweede ack
    // en geen tweede geluid opleveren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationId]);

  // ── Stilte tijdens een gesprek ───────────────────────────────────────────
  //
  // Zowel de chime als de voorleesstem. Praten door een telefoongesprek heen is niet
  // hinderlijk maar onbruikbaar: op een handsfree wandtablet komt beide uit dezelfde
  // luidspreker en verstaat de bewoner geen van tweeën.
  useEffect(() => {
    if (!paused) return;
    NotificationStore.stopSound();
    // Via de store en niet rechtstreeks via de bridge: de store houdt bij WELKE melding er
    // gesproken wordt (T-5.04, wachtrij van één), en een stop die daarbuiten omgaat laat
    // die boekhouding achter met een stem die volgens hem nog praat.
    void NotificationStore.stopTts();
  }, [paused]);

  // Bij het verlaten van het scherm stopt alles wat nog klinkt.
  useEffect(
    () => () => {
      NotificationStore.stopSound();
      void NotificationStore.stopTts();
    },
    [],
  );

  // ── De aftelling ─────────────────────────────────────────────────────────
  //
  // Er wordt VERSTREKEN TIJD afgetrokken in plaats van een vaste stap per tik: een WebView
  // die zijn timers knijpt (achtergrond, dim, energiebesparing) zou anders een aftelling van
  // dertig seconden over twee minuten uitsmeren. En tijdens een gesprek wordt er niets
  // afgetrokken — daar gaat het bij T-3.04 om.
  useEffect(() => {
    lastTickAt.current = Date.now();
    const timer = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastTickAt.current;
      lastTickAt.current = now;

      // `expiresAt` telt ALTIJD door, ook tijdens een gesprek. Schermtijd en geldigheid zijn
      // twee verschillende dingen (FR-10): een melding die intussen niet meer relevant is,
      // wordt na het gesprek niet alsnog opgediend.
      const expiresAt = new Date(payload.expiresAt).getTime();
      if (Number.isFinite(expiresAt) && now >= expiresAt) {
        clearInterval(timer);
        close(false);
        return;
      }

      if (paused || totalMs == null) return;

      setRemainingMs((prev) => {
        if (prev == null) return prev;
        const next = prev - elapsed;
        if (next <= 0) {
          clearInterval(timer);
          // Vanzelf verdwijnen is GEEN `acknowledged`: de bewoner heeft niets bevestigd.
          // Die twee door elkaar halen maakt de statistiek "hoeveel meldingen worden echt
          // gelezen" waardeloos.
          close(false);
          return 0;
        }
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [paused, totalMs, payload.expiresAt, close]);

  // ── Een falend achtergronditem (T-3.09) ──────────────────────────────────
  const onItemError = useCallback(
    (index: number, _item: unknown, message: string) => {
      NotificationStore.reportMediaError(notificationId, `background[${index}]: ${message}`);
    },
    [notificationId],
  );

  const fraction =
    totalMs == null || remainingMs == null ? 1 : Math.max(0, Math.min(1, remainingMs / totalMs));
  const secondsLeft = remainingMs == null ? null : Math.ceil(remainingMs / 1000);

  const background = Array.isArray(payload.background) ? payload.background : [];
  /** Ligt er beeld onder het paneel? Bepaalt of de sluier zin heeft. */
  const hasMedia = background.length > 0;

  // SHARED-FIRST: `requestStream` bestaat al (T-3.12) en doet precies wat hier nodig is —
  // vragen en het antwoord van de server accepteren. Er komt hier dus GEEN tweede
  // streamaanvraag naast die van de videofoon.
  //
  // `false` schakelt alleen het VOORLADEN van de cameralijst uit; die lijst is voor het
  // deurbelscherm en heeft hier geen betekenis. De aanvraagfunctie blijft gewoon bruikbaar.
  const { requestStream: requestCameraStream } = useDoorbellCameras(false);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="caro-fullpage-title"
      data-testid="notification-fullpage"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDEX.NOTIFICATION_FULLPAGE,
        backgroundColor: 'var(--caro-card-bg)',
        color: 'var(--caro-text)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Media edge-to-edge. Zonder achtergrond blijft dit vlak leeg en vult het paneel
          het scherm — een melding zonder afbeelding is geen halve melding. */}
      {/* ── HET BEELD KRIJGT HET HELE SCHERM ──────────────────────────────────
          Eerst deelden media en paneel de hoogte via flex, dus het beeld stopte waar de
          tekst begon -- op een 800x480-tablet bleef daar weinig van over.

          Nu vult het beeld het volledige scherm en LIGT het paneel eroverheen. Dat kan
          precies omdat dat paneel glas is: je kijkt er doorheen, dus de onderste strook
          beeld gaat niet verloren maar schemert door de tekst heen. Zonder glas zou dit
          een verslechtering zijn -- dan verstopte het paneel een deel van het beeld. */}
      <div style={{ position: 'absolute', inset: 0 }}>
        {background.length > 0 && (
          <NotificationBackgroundCarousel
            items={background}
            onItemError={onItemError}
            requestCameraStream={requestCameraStream}
          />
        )}
        {/* Een donkere sluier over het beeld. Niet voor het contrast van de TEKST — die
            staat op het opake paneel hieronder — maar zodat de rand tussen beeld en paneel
            rustig is in plaats van hard. */}
        {background.length > 0 && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.25)',
              // ⚠ ZONDER DIT SLIKT DEZE LAAG ELKE AANRAKING.
              //
              // Hij ligt over de volle carrousel en wordt NA de pijlen en stippen gerenderd,
              // dus hij vangt elke klik en elke veeg. Je ziet de knoppen er dwars doorheen
              // en raakt ze nooit aan -- gemeten op de tablet, 2026-09-08: vier stippen
              // zichtbaar, wisselen onmogelijk.
              //
              // Hij is puur decoratief (`aria-hidden`), dus hij hoort geen aanrakingen te
              // ontvangen. Elke volle-schermlaag boven interactieve inhoud heeft dit nodig.
              pointerEvents: 'none',
            }}
          />
        )}
      </div>

      {/* ── Het contrastpaneel ────────────────────────────────────────────────
          Opaak `--caro-card-bg` met `--caro-text`. Zie de kop: het thema garandeert dat
          paar, een halfdoorzichtig vlak zou het contrast laten afhangen van de foto
          erachter. */}
      <div
        style={{
          // Zwevend over het beeld in plaats van eronder. Zie de toelichting hierboven.
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          // ── HALF DOORZICHTIG, MAAR ALLEEN BOVEN BEELD ────────────────────────
          //
          // Ligt er een camerabeeld onder, dan is dit paneel een sluier: de bewoner ziet
          // wie er aan de deur staat ook waar de tekst staat. Zonder achtergrond zou dat
          // averechts werken -- dan is er niets om doorheen te kijken en levert een
          // doorzichtig vlak alleen leesbaarheid in.
          //
          // Geen `color-mix()`: deze tablets draaien Chromium 84 en kennen dat niet. Vandaar
          // een rechtstreekse rgba, met lichte tekst omdat de sluier donker is.
          // Bewust de DONKERE glasvariant, ook in lichte modus: wat hieronder ligt is
          // videobeeld, geen pagina. Melkwit glas met donkere tekst zou daar onleesbaar zijn.
          // De waarde komt uit de gedeelde theme (`--caro-glass-*`), niet uit een losse rgba.
          backgroundColor: hasMedia
            ? 'var(--caro-glass-bg-dark, rgba(22,22,26,0.62))'
            : 'var(--caro-card-bg)',
          backdropFilter: hasMedia ? 'var(--caro-glass-filter)' : 'none',
          // Zonder voorvoegsel is er op deze WebView geen glas maar een doorzichtig vlak,
          // en dat is erger dan geen glas: dan verdwijnt de leesbaarheid.
          WebkitBackdropFilter: hasMedia ? 'var(--caro-glass-filter)' : 'none',
          color: hasMedia ? '#fff' : 'var(--caro-text)',
          borderTop: `8px solid ${accent}`,
          padding: '14px 20px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          boxShadow: 'var(--caro-raised-shadow, 0 -12px 32px rgba(0,0,0,0.35))',
          // Het paneel groeit niet verder dan de helft van het scherm en krimpt niet onder
          // wat de knoppen nodig hebben. Wat overblijft is voor het beeld. Bij een lange
          // tekst scrolt dit paneel; het beeld levert geen ruimte meer in.
          maxHeight: '38%',
          flexShrink: 0,
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span aria-hidden="true" style={{ fontSize: 32, color: accent, lineHeight: 1 }}>
            {PRIORITY_ICON[payload.priority]}
          </span>
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: accent,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {priorityWord(t, payload.priority)}
          </span>
          <span style={{ flex: 1 }} />
          {paused && (
            // Zichtbaar maken DAT er gepauzeerd is. Een aftelbalk die stilstaat zonder
            // uitleg leest als een vastgelopen scherm.
            <span
              data-testid="fullpage-paused"
              style={{ fontSize: 16, opacity: 0.75, fontWeight: 600 }}
            >
              {t.notifications.pausedDuringCall}
            </span>
          )}
          {!paused && secondsLeft != null && (
            <span
              role="timer"
              aria-live="off"
              aria-label={tf(t.notifications.closesIn, { n: secondsLeft })}
              style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
            >
              {secondsLeft}
            </span>
          )}
        </div>

        <h1
          id="caro-fullpage-title"
          style={{ margin: 0, fontSize: 34, lineHeight: 1.2, fontWeight: 700 }}
        >
          {localized.title}
        </h1>

        {localized.body && (
          <p style={{ margin: 0, fontSize: 22, lineHeight: 1.5, opacity: 0.92 }}>{localized.body}</p>
        )}

        {/* ── De aftelbalk (T-3.03) ────────────────────────────────────────────
            Naast het getal hierboven, niet in plaats daarvan: een balk alleen is voor een
            deel van dit publiek te subtiel, een getal alleen te abstract. */}
        {totalMs != null && (
          <div
            data-testid="fullpage-countdown"
            aria-hidden="true"
            style={{
              height: 10,
              borderRadius: 5,
              backgroundColor: 'rgba(128,128,128,0.25)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${fraction * 100}%`,
                backgroundColor: accent,
                transition: `width ${TICK_MS}ms linear`,
              }}
            />
          </div>
        )}

        {/* Fase 4. Boven "Sluiten", om dezelfde reden als in de dialoog.

            `dismiss`-acties worden ERUIT gefilterd: dit scherm heeft hieronder al een vaste
            sluitknop, en een flowauteur die er zelf ook een instelt krijgt er anders twee.
            Dat gebeurde ook echt (2026-09-08). De vaste knop blijft staan en wordt niet
            vervangen door de ingestelde: hij is de enige gegarandeerde uitgang van een
            scherm dat alles bedekt, en die mag niet van configuratie afhangen. */}
        {/* Acties en de vaste sluitknop op ÉÉN rij. Onder elkaar aten ze twee keer de
            hoogte op die het camerabeeld nodig heeft. `flexWrap` vangt het geval op dat er
            drie acties staan en het samen niet past: dan valt de sluitknop terug naar een
            eigen regel in plaats van te verschrompelen. */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <NotificationActions
              notificationId={notificationId}
              actions={(payload.actions || []).filter((a) => a.type !== 'dismiss')}
              onClose={(ack) => close(ack)}
              minHeight={72}
              labels={localized.actionLabels}
            />
          </div>

          <button
            type="button"
            onClick={() => close(true)}
            data-testid="fullpage-close"
            style={{
            // ≥ 80px — de emergency-touchtargetnorm uit CLAUDE.md. Dit is de enige uitgang
            // van een scherm dat alles bedekt; hem missen is erger dan hem te groot maken.
            minHeight: 80,
            padding: '18px 24px',
            borderRadius: 16,
            border: 'none',
            backgroundColor: accent,
            color: 'var(--caro-card-bg)',
            fontSize: 24,
            fontWeight: 700,
            cursor: 'pointer',
          }}
          >
            {t.notifications.close}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NotificationFullPage;
